// TourProvider — state + persistence + navigation for the guided tour.
//
// Persists across refreshes under localStorage key `commons.tour.v1`, so a
// visitor who closes the tab mid-tour lands back on the same stop. Because
// our anonymous session ID is per-browser, tour progress naturally
// isolates to that browser too.

import { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { useCommons } from "../lib/store";
import { consumeAutoTourFlag } from "../lib/session";
import { STEPS, resetTourStickyState, restoreTourStickyState, getStickyProjectId } from "./steps";

const TOUR_KEY = "commons.tour.v1";

const TourCtx = createContext(null);

function loadState() {
  if (typeof window === "undefined") return {};
  try {
    return JSON.parse(localStorage.getItem(TOUR_KEY) || "{}");
  } catch (_) { return {}; }
}
function saveState(s) {
  if (typeof window !== "undefined") {
    localStorage.setItem(TOUR_KEY, JSON.stringify(s));
  }
}

export function TourProvider({ children }) {
  const { data, user, resetAll } = useCommons();
  const nav = useNavigate();
  const location = useLocation();

  const persisted = useMemo(loadState, []);
  const autoTourRequested = useMemo(() => consumeAutoTourFlag(), []);
  const [active, setActive] = useState(!!persisted.active);
  const [step, setStep] = useState(
    typeof persisted.step === "number" ? persisted.step : 0
  );
  const [freshOffered, setFreshOffered] = useState(!!persisted.freshOffered);

  // Restore sticky tour project on mount so a mid-tour reload keeps
  // the same project across library → Project Detail → Roots.
  useEffect(() => {
    if (persisted.stickyProjectId) restoreTourStickyState(persisted.stickyProjectId);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // "waitingForInteraction" — true when the current step's real Commons-
  // native interaction has not yet happened. Applies to:
  //   • "try-it" stop     → visitor must tap "I'm in" (signalled by
  //                          ProjectDetail via markCommitmentMade)
  //   • steps flagged `interactive: true` → visitor must tap the halo
  //     target itself. TourOverlay attaches a click listener to the
  //     resolved target and calls markInteractionDone() on click.
  // The tour never auto-advances past these waits. Skip / Escape /
  // close still work.
  const [waitingForInteraction, setWaitingForInteraction] = useState(false);
  const stepRef = useRef(step);
  stepRef.current = step;

  useEffect(() => {
    saveState({ active, step, freshOffered, stickyProjectId: getStickyProjectId() });
  }, [active, step, freshOffered]);

  const currentStep = active ? STEPS[step] : null;

  const start = useCallback((atIndex = 0) => {
    if (atIndex === 0) resetTourStickyState();
    setActive(true);
    setStep(atIndex);
    setFreshOffered(true);
  }, []);

  const autoTourFiredRef = useRef(false);
  useEffect(() => {
    if (!autoTourRequested || autoTourFiredRef.current) return;
    if (!data || !user) return;
    autoTourFiredRef.current = true;
    start(0);
  }, [autoTourRequested, data, user, start]);

  const close = useCallback(() => {
    resetTourStickyState();
    setActive(false);
    setFreshOffered(true);
    setWaitingForInteraction(false);
  }, []);

  const goToStep = useCallback((i) => {
    setStep(Math.max(0, Math.min(STEPS.length - 1, i)));
    setWaitingForInteraction(false);
  }, []);

  const next = useCallback(() => {
    setWaitingForInteraction(false);
    setStep((s) => Math.min(STEPS.length - 1, s + 1));
  }, []);

  const back = useCallback(() => {
    setWaitingForInteraction(false);
    setStep((s) => Math.max(0, s - 1));
  }, []);

  const skip = useCallback(() => close(), [close]);

  const dismissFreshOffer = useCallback(() => setFreshOffered(true), []);

  // Called by ProjectDetail after a successful pledge on the "try-it"
  // stop. Waits ~1.2s for the celebration animation to breathe, then
  // advances to the next stop (which stays on Project Detail and
  // highlights the Roots pill).
  const markCommitmentMade = useCallback(() => {
    if (!active) return;
    const s = STEPS[stepRef.current];
    if (s?.id === "try-it") {
      setWaitingForInteraction(false);
      setTimeout(() => {
        setStep((cur) => Math.min(STEPS.length - 1, cur + 1));
      }, 1200);
    }
  }, [active]);

  // Called by TourOverlay when the visitor taps the current step's
  // halo target and the step is flagged `interactive: true`. We
  // advance the tour — the visitor's own click is what performs the
  // Commons-native navigation (category door, project card, Roots
  // pill, You ✨). No preventDefault, no synthetic navigation.
  const markInteractionDone = useCallback(() => {
    if (!active) return;
    const s = STEPS[stepRef.current];
    if (s?.interactive) {
      setWaitingForInteraction(false);
      setStep((cur) => Math.min(STEPS.length - 1, cur + 1));
    }
  }, [active]);

  // Navigate to the step's route on step change and arm the interaction
  // wait for interactive / try-it steps.
  useEffect(() => {
    if (!active || !currentStep || !data || !user) return;
    const targetRoute =
      typeof currentStep.route === "function"
        ? currentStep.route({ data, user })
        : currentStep.route;
    if (targetRoute && targetRoute !== location.pathname) {
      nav(targetRoute);
    }
    setWaitingForInteraction(
      currentStep.id === "try-it" || currentStep.interactive === true
    );
  }, [active, step, currentStep, data, user, nav, location.pathname]);

  const restartFresh = useCallback(async () => {
    resetTourStickyState();
    setActive(false);
    setStep(0);
    setFreshOffered(false);
    await resetAll();
    nav("/", { replace: true });
  }, [resetAll, nav]);

  const restartInPlace = useCallback(() => {
    resetTourStickyState();
    setStep(0);
    setActive(true);
  }, []);

  const value = {
    active,
    step,
    total: STEPS.length,
    currentStep,
    freshOffered,
    // Back-compat alias kept for TourOverlay's older code paths.
    waitingForCommit: waitingForInteraction,
    waitingForInteraction,
    start,
    close,
    next,
    back,
    skip,
    goToStep,
    restartFresh,
    restartInPlace,
    dismissFreshOffer,
    markCommitmentMade,
    markInteractionDone,
  };

  return <TourCtx.Provider value={value}>{children}</TourCtx.Provider>;
}

export function useTour() {
  const ctx = useContext(TourCtx);
  if (!ctx) throw new Error("useTour outside provider");
  return ctx;
}
